import { Button } from "@nexus/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useIamPermission, usePermissions } from "@/features/auth/hooks/usePermissions";
import { OrdersList } from "@/features/marketplace/components/orders-list/OrdersList";
import { OrdersListSkeleton } from "@/features/marketplace/components/orders-list/OrdersListSkeleton";
import type { MarketplaceOrder } from "@/features/marketplace/types/marketplace.types";

import { SentToVendorTabPlaceholder } from "../../workspace/tabs/SentToVendorTab/SentToVendorTabPlaceholder.js";
import { scopeMarketplaceOrders, type OpportunityHubSelection } from "./hub-chrome-selection.js";
import type { OpportunityHubListState } from "./OpportunityHubListFooter.js";

export function OpportunityHubProductionPanel({
  orders,
  selection,
  tenantId,
  listState,
  onGoToOpportunities,
}: {
  orders: readonly MarketplaceOrder[];
  selection: OpportunityHubSelection;
  tenantId: string | null;
  listState?: OpportunityHubListState;
  onGoToOpportunities: () => void;
}) {
  const { t } = useTranslation();
  const perms = usePermissions();
  const canListOrders =
    useIamPermission(perms.MARKETPLACE_VIEW_PAGE) ||
    useIamPermission(perms.MARKETPLACE_LIST_ORDERS);
  const scopedOrders = useMemo(
    () => scopeMarketplaceOrders(orders, selection),
    [orders, selection],
  );
  const stateScope = [
    "hub",
    selection.sets.join(","),
    selection.opportunities.join(","),
    selection.scripts.join(","),
  ].join(":");

  if (!tenantId || !canListOrders) {
    return <SentToVendorTabPlaceholder onGoToOpportunities={onGoToOpportunities} />;
  }

  if (listState?.isLoading && orders.length === 0) {
    return <OrdersListSkeleton />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <OrdersList
        orders={scopedOrders}
        tenantId={tenantId}
        variant="in-production"
        navigationMode="embedded"
        stateScope={stateScope}
      />
      {listState?.hasMore ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-center"
          disabled={listState.isLoadingMore}
          onClick={() => listState.onLoadMore?.()}
        >
          {t("marketplace.orders.list.loadMore", "Load more")}
        </Button>
      ) : null}
    </div>
  );
}
